/**
 * PlanRendererArrangeMode — renders the floor plan SVG in Arrange mode.
 *
 * Responsibilities:
 *  - Mount the cached base SVG clone into the container exactly once per
 *    `svgElement`, then mutate it in place as the user cycles compartment
 *    variants. Mutating in place (vs re-cloning on every selection) keeps
 *    zoom/pan/scroll stable and avoids flicker.
 *  - Eagerly fetch every variant fragment on mode entry via
 *    `useCompartmentVariants`, then forward cached nodes to
 *    `applyCompartmentVariants`.
 *
 * Arrange mode is 2D-only and ignores color schemes — the base SVG is rendered
 * in its neutral default style. Interactions (hover / click-to-cycle) live in
 * a follow-up step and will be wired through pointer events on `containerRef`.
 *
 * @param {{ svgElement: SVGElement|null, svgDimensions: { width: number, height: number } }} props
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { useProduct } from '../../index.js'

import { useArrangeModeInteractions } from '../../hooks/useArrangeModeInteractions.js'
import { useArrangeModePreview } from '../../hooks/useArrangeModePreview.js'
import { usePlanNorthRotation } from '../../hooks/usePlanNorthRotation.js'
import {
  applyCompartmentVariants,
  syncNoVariantsClass,
  syncHoveredCompartmentClass,
} from '../../utils/svgArrange.js'

import { VersionPopover } from './VersionPopover.jsx'

import styles from './PlanRendererArrangeMode.module.scss'

// CSS class names passed to the interactions hook for direct DOM classList ops.
const CLASS_NAMES = {
  compartmentHovered:     styles.compartmentHovered,
  compartmentNoVariants:  styles.compartmentNoVariants,
}

export function PlanRendererArrangeMode({ svgElement, svgDimensions, getVariantNode }) {
  const containerRef    = useRef(null)
  const wrapperRef      = useRef(null)
  const mountedBaseRef  = useRef(null)            // last SVGElement used as mount source
  const mountedSvgRef   = useRef(null)            // the live clone currently inside the container
  const prevVariantsRef = useRef(null)            // last selectedVariants applied to the mounted clone

  const { selection, viewerState, viewerActions } = useProduct()
  const { product } = selection
  const { selectedVariants, variantsCount, hoveredCompartmentId, rotatedToNorth } = viewerState
  const { cycleCompartmentVariant, setHoveredCompartmentId, setAllVariants } = viewerActions

  // ── Hover popover + interaction tracking ─────────────────────────────────
  // Cursor position seed for the cursor-following Popover; reset on hover-out.
  // `userInteracted` flips on first hover/click and stops the preview animation.

  const [popoverPos, setPopoverPos] = useState(null)
  const [userInteracted, setUserInteracted] = useState(false)

  // ── Rotate-to-North ───────────────────────────────────────────────────────

  const { wrapperStyle, rendererStyle } = usePlanNorthRotation({
    rotatedToNorth,
    azimuth: product?.azimuth ?? null,
    svgDimensions,
    wrapperRef,
  })

  const handleHoverChange = useCallback((n, event) => {
    if (!userInteracted) setUserInteracted(true)
    if (n != null && event) {
      setPopoverPos({ x: event.clientX, y: event.clientY })
    }
    setHoveredCompartmentId(n)
  }, [userInteracted, setHoveredCompartmentId])

  const handleClickInteraction = useCallback((n) => {
    if (!userInteracted) setUserInteracted(true)
    cycleCompartmentVariant(n)
  }, [userInteracted, cycleCompartmentVariant])

  // ── On-entry preview ─────────────────────────────────────────────────────
  // Plays once per product per session — animates through every alternative
  // variant, then settles back to all-zero. Aborts on first user interaction.

  useArrangeModePreview({
    productKey: product?.uuid ?? product?.id_cad ?? null,
    variantsCount,
    enabled: typeof getVariantNode === 'function' && svgElement != null,
    userInteracted,
    setAllVariants,
  })

  // ── Interactions (hover → context, click → cycle variant) ─────────────────

  const { handlePointerMove, handlePointerLeave, handleClick } = useArrangeModeInteractions({
    svgElement,
    variantsCount,
    setHoveredCompartmentId: (n, event) => handleHoverChange(n, event),
    cycleCompartmentVariant: handleClickInteraction,
  })

  // ── Mount / full remount ─────────────────────────────────────────────────
  // Runs only when the base SVGElement reference changes (new product, new
  // tier, or first mount). Clones the base, applies the current variant
  // selection in one pass, and inserts the clone into the container.

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.innerHTML = ''
    mountedBaseRef.current  = null
    mountedSvgRef.current   = null
    prevVariantsRef.current = null

    if (!svgElement) return

    const clone = svgElement.cloneNode(true)
    applyCompartmentVariants({
      targetSvg: clone,
      baseSvgElement: svgElement,
      selectedVariants,
      getVariantNode,
      // No prevVariants on first mount — apply everything.
    })
    container.appendChild(clone)

    // Apply per-compartment interactivity classes to the freshly-mounted SVG.
    // (Done here, after appendChild, to avoid an empty-container race when
    // running these as separate effects in declaration order.)
    syncNoVariantsClass(container, variantsCount, CLASS_NAMES.compartmentNoVariants)
    syncHoveredCompartmentClass(container, hoveredCompartmentId, CLASS_NAMES.compartmentHovered)

    mountedBaseRef.current  = svgElement
    mountedSvgRef.current   = clone
    prevVariantsRef.current = selectedVariants
  }, [svgElement]) // eslint-disable-line react-hooks/exhaustive-deps -- selectedVariants/getVariantNode handled by the patch effect below

  // ── Hover-class sync ─────────────────────────────────────────────────────
  // Re-runs whenever `hoveredCompartmentId` changes (driven either by local
  // pointer events or by an external surface like CompartmentsPane).

  useEffect(() => {
    if (mountedBaseRef.current !== svgElement) return
    syncHoveredCompartmentClass(
      containerRef.current,
      hoveredCompartmentId,
      CLASS_NAMES.compartmentHovered,
    )
  }, [hoveredCompartmentId, svgElement])

  // ── No-variants class re-sync ────────────────────────────────────────────
  // variantsCount changes whenever the active product changes — but on a
  // product change, svgElement also changes (different floor plan), so the
  // mount effect above already re-applies the classes. This effect handles
  // the edge case where variantsCount changes without svgElement changing
  // (e.g. live data reload).

  useEffect(() => {
    if (mountedBaseRef.current !== svgElement) return
    syncNoVariantsClass(
      containerRef.current,
      variantsCount,
      CLASS_NAMES.compartmentNoVariants,
    )
  }, [variantsCount, svgElement])
  // (the mount effect above will catch up on its own).

  useEffect(() => {
    if (mountedBaseRef.current !== svgElement) return
    const mountedSvg = mountedSvgRef.current
    if (!mountedSvg || !svgElement) return

    applyCompartmentVariants({
      targetSvg: mountedSvg,
      baseSvgElement: svgElement,
      selectedVariants,
      getVariantNode,
      prevVariants: prevVariantsRef.current,
    })
    prevVariantsRef.current = selectedVariants
  }, [selectedVariants, svgElement, getVariantNode])

  // ── Render ───────────────────────────────────────────────────────────────

  const hoveredCompartment = hoveredCompartmentId != null
    ? (product?.compartments?.find((c) => Number(c.number) === hoveredCompartmentId) ?? null)
    : null
  const hoveredIdx       = hoveredCompartmentId != null ? hoveredCompartmentId - 1 : -1
  const hoveredTotal     = hoveredIdx >= 0 ? (variantsCount[hoveredIdx] ?? 1) : 1
  const hoveredCurrent   = hoveredIdx >= 0 ? (selectedVariants[hoveredIdx] ?? 0) : 0
  const isPopoverOpen    = hoveredCompartmentId != null && popoverPos != null

  return (
    <>
      <div ref={wrapperRef} className={styles.wrapper} style={wrapperStyle}>
        <div
          ref={containerRef}
          className={`${styles.renderer} bim-plan`}
          data-variant="2d"
          style={rendererStyle}
          aria-label="Rzut lokalu — tryb aranżacji"
          onPointerMove={handlePointerMove}
          onPointerLeave={handlePointerLeave}
          onClick={handleClick}
        />
      </div>
      <VersionPopover
        compartment={hoveredCompartment}
        position={popoverPos}
        isOpen={isPopoverOpen}
        onClose={() => setPopoverPos(null)}
        variantsTotal={hoveredTotal}
        variantCurrent={hoveredCurrent}
      />
    </>
  )
}
